import React, { useEffect, useState } from 'react';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import Chart from 'react-apexcharts';
import { Box, Grid, Paper, Typography, IconButton } from '@mui/material';

import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getUserAnalytics } from '../../../../redux/features/taskManagementSlice';
import { AccessTime, Add, Close } from '@mui/icons-material';
import { Button, Stack, Tooltip } from '@mui/material';

// 🌈 Glass style (slightly reduced padding)
const glass = {
  borderRadius: '22px',
  background: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
  p: 2, // reduced from 2.5
};

const PRIORITY_STYLES = {
  Critical: {
    color: '#ef4444',
    bg: 'linear-gradient(135deg, #ef4444, #b91c1c)',
    text: '#fff',
    glow: '0 10px 30px rgba(239,68,68,0.5)',
  },
  High: {
    color: '#f97316',
    bg: 'linear-gradient(135deg, #f97316, #fb923c)',
    text: '#fff',
    glow: '0 8px 20px rgba(249,115,22,0.4)',
  },
  Medium: {
    color: '#eab308',
    bg: 'linear-gradient(135deg, #fde68a, #facc15)',
    text: '#000',
    glow: '0 6px 18px rgba(234,179,8,0.3)',
  },
  Low: {
    color: '#22c55e',
    bg: 'linear-gradient(135deg, #bbf7d0, #4ade80)',
    text: '#000',
    glow: '0 6px 18px rgba(34,197,94,0.3)',
  },
};

const UserTaskAnalytics = () => {
  // Date range - opt-in, hidden and unset until the user turns it on, same
  // as My Tasks. Nothing is hidden by default the way the old mandatory
  // month filter did.
  const [dateRangeEnabled, setDateRangeEnabled] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const { userAnalytics, userAnalyticsLoading } = useSelector((state) => state.taskManagement);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getUserAnalytics({
        from_date: dateRangeEnabled && fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : undefined,
        to_date: dateRangeEnabled && toDate ? dayjs(toDate).format('YYYY-MM-DD') : undefined,
      })
    );
  }, [dateRangeEnabled, fromDate, toDate]);

  const apiData = userAnalytics || {};

  // ✅ Safe mapping
  const data = {
    total_tasks: apiData.total_tasks || 0,
    pending_tasks: apiData.pending_tasks || 0,
    completed_tasks: apiData.completed_tasks || 0,
    inprogress_tasks: apiData.inprogress_tasks || 0,
    overdue_tasks: apiData.overdue_tasks || 0,
  };

  const priorityData = apiData.priorityData || [];

  const completionRate = data.total_tasks > 0 ? Math.round((data.completed_tasks / data.total_tasks) * 100) : 0;

  // 🎯 Radial
  const radialOptions = {
    chart: { type: 'radialBar' },
    plotOptions: {
      radialBar: {
        hollow: { size: '65%' },
        track: { background: '#eef2ff' },
        dataLabels: {
          value: {
            fontSize: '28px', // slightly smaller
            fontWeight: 700,
          },
        },
      },
    },
    colors: ['#4f46e5'],
    labels: ['Completion'],
  };

  // 🍩 Donut
  const donutOptions = {
    chart: { type: 'donut' },
    labels: ['Pending', 'Completed', 'In Progress', 'Overdue'],
    colors: ['#f59e0b', '#22c55e', '#3989c2', '#ef4444'],
    legend: { position: 'bottom' },
    stroke: { width: 0 },
  };

  const donutSeries = [data.pending_tasks, data.completed_tasks, data.inprogress_tasks, data.overdue_tasks];

  // 📊 Bar (dynamic from API)
  const barOptions = {
    chart: { toolbar: { show: false } },

    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '45%',
        distributed: true, // 🔥 key for different colors
      },
    },

    xaxis: {
      categories: ['Total', 'Pending', 'Completed', 'In Progress', 'Overdue' ],
    },

    // 🎨 one color per category (order matters)
    colors: [
      '#6366f1', // Total → primary blue
      '#f59e0b', // Pending → yellow
      '#22c55e', // Completed → green
      '#3989c2', // In Progress → blue
      '#ef4444', // Overdue → red
    ],

    grid: { borderColor: '#e5e7eb' },
  };

  const barSeries = [
    {
      name: 'Tasks',
      data: [data.total_tasks, data.pending_tasks, data.completed_tasks, data.inprogress_tasks, data.overdue_tasks],
    },
  ];

  // 🎯 KPI Cards
  const cards = [
    {
      label: 'Total Tasks',
      value: data.total_tasks,
      icon: <AutoGraphIcon />,
      glow: 'radial-gradient(circle,#6366f133,transparent)',
    },
    {
      label: 'Completed',
      value: data.completed_tasks,
      icon: <TaskAltIcon />,
      glow: 'radial-gradient(circle,#22c55e33,transparent)',
    },
    {
      label: 'Pending',
      value: data.pending_tasks,
      icon: <PendingActionsIcon />,
      glow: 'radial-gradient(circle,#f59e0b33,transparent)',
    },
    {
      label: 'In Progress',
      value: data.inprogress_tasks,
      icon: <AccessTime />,
      glow: 'radial-gradient(circle,#3989c233,transparent)',
    },
    {
      label: 'Overdue',
      value: data.overdue_tasks,
      icon: <WarningAmberIcon />,
      glow: 'radial-gradient(circle,#ef444433,transparent)',
    },
  ];

  const getPriorityIcon = (name) => {
    switch (name) {
      case 'Critical':
        return <WarningAmberIcon />;
      case 'High':
        return <AutoGraphIcon />;
      case 'Medium':
        return <PendingActionsIcon />;
      default:
        return <TaskAltIcon />;
    }
  };

  return (
    <GlobalWrapper
      title="Task Analytics"
      actions={
        <Box mb={2} display="flex" justifyContent="flex-end">
          {!dateRangeEnabled ? (
            <Button
              size="small"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={() => setDateRangeEnabled(true)}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B' }}
            >
              Filter by created date
            </Button>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DatePicker
                  label="From"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD MMM YYYY"
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="To"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD MMM YYYY"
                  minDate={fromDate || undefined}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <Tooltip title="Clear date filter">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDateRangeEnabled(false);
                      setFromDate(null);
                      setToDate(null);
                    }}
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </LocalizationProvider>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          minHeight: '100vh',
          p: 3,
          borderRadius: 2,
          background: 'radial-gradient(circle at top left,#eef2ff,#f8fafc)',
        }}
      >
        {/* 📅 Date Picker */}

        {/* 🚨 Priority Cards - Eye Catching Modern */}
        <Box
          display="flex"
          justifyContent="center" // 🔥 center horizontally
          alignItems="center"
          flexWrap="wrap"
          gap={1.5}
          mb={2}
        >
          {priorityData.map((p, i) => {
            const style = PRIORITY_STYLES[p.approval_priority_name] || PRIORITY_STYLES.Low;

            return (
              <Box
                key={i}
                sx={{
                  px: 2,
                  py: 0.7,
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,

                  border: `1.5px solid ${style.color}`,
                  color: style.color,

                  // ✨ soft glass + glow
                  background: `linear-gradient(135deg, ${style.color}12, transparent)`,
                  backdropFilter: 'blur(6px)',

                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: 0.3,

                  boxShadow: `0 4px 12px ${style.color}22`,

                  transition: '0.25s',
                  cursor: 'pointer',

                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.05)',
                    background: `${style.color}20`,
                    boxShadow: `0 6px 18px ${style.color}40`,
                  },
                }}
              >
                {/* 🔥 glowing dot */}
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: style.color,
                    boxShadow: `0 0 8px ${style.color}`,
                  }}
                />

                {/* label */}
                {p.approval_priority_name}

                {/* count badge */}
                <Box
                  sx={{
                    px: 1,
                    py: '2px',
                    borderRadius: '999px',
                    fontSize: 11,
                    fontWeight: 700,

                    background: `${style.color}20`,
                    color: style.color,
                  }}
                >
                  {p.count || 0}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* 🔥 KPI Cards */}
        <Grid container spacing={3}>
          {cards.map((c, i) => (
            <Grid item sx={{
        width: {
          xs: '100%',
          sm: '50%',
          md: '20%', // 5 cards in a row
        },
      }} key={i}>
              <Paper sx={{ ...glass, position: 'relative', overflow: 'hidden' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    width: 100,
                    height: 120,
                    background: c.glow,
                    top: -40,
                    right: -40,
                  }}
                />

                <Box display="flex" justifyContent="space-between">
                  <Box
                    sx={{
                      background: '#fff',
                      p: 1,
                      borderRadius: '50%',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                    }}
                  >
                    {c.icon}
                  </Box>

                  <IconButton size="small">
                    <ArrowOutwardIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography mt={2} variant="body2">
                  {c.label}
                </Typography>

                <Typography variant="h5" fontWeight="bold">
                  {c.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* 📊 Charts */}
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={4}>
            <Paper sx={glass}>
              <Typography mb={1}>Completion</Typography>
              <Chart options={radialOptions} series={[completionRate]} type="radialBar" height={250} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={glass}>
              <Typography mb={1}>Distribution</Typography>
              <Chart options={donutOptions} series={donutSeries} type="donut" height={250} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={glass}>
              <Typography mb={1}>Task Breakdown</Typography>
              <Chart options={barOptions} series={barSeries} type="bar" height={250} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </GlobalWrapper>
  );
};

export default UserTaskAnalytics;
